import moment from 'moment'
import { pendingSelector } from 'api/selectors'
import { createSelector } from 'reselect'
import { userSelector } from 'auth/selectors'
import { v4 } from 'uuid'

import {
    INVOICE_PAYMENT_PROGRESS,
    ASSIGN_PARKER_PROGRESS,
    REMOVE_PARKER_PROGRESS,
    SAVE_PAD_DATA_PROGRESS,
} from './constants'
import { vehiclesEntityToArray } from 'dashboard-subscriptions/utils'

const stateSelector = state => state.dashboardSubscriptions

export const subscriptionsHistorySelector = createSelector(stateSelector, state => state.subscriptions)

export const subscriptionsHistoryPendingSelector = createSelector(
    pendingSelector,
    pending => pending['subscriptionHistory']
)

export const getWaitListSelectorPendingSelector = createSelector(pendingSelector, pending => pending['waitList'])

export const subscriptionInvoicesLoadingSelector = createSelector(
    pendingSelector,
    pending => pending['subscriptionInvoices']
)

export const subscriptionReceiptsLoadingSelector = createSelector(
    pendingSelector,
    pending => pending['subscriptionReceipts']
)

export const subscriptionInvoicesSelector = createSelector(stateSelector, state =>
    state.subscriptionInvoices.sort((a, b) => moment(b.dueDate).diff(moment(a.dueDate)))
)

export const subscriptionReceiptsSelector = createSelector(stateSelector, state => state.subscriptionReceipts)

export const updateSubscriptionPendingSelector = createSelector(
    pendingSelector,
    pending => pending['updateSubscription']
)

export const paymentMethodsSelector = createSelector(userSelector, ({ creditCards = [], padMethods = [] }) =>
    [...creditCards, ...padMethods].map(c => ({
        ...c,
        value: c.id,
    }))
)

export const waitListSelector = createSelector(stateSelector, state => state.waitList)

/** Products visible for the subscription modal: currently active by date range, else earliest upcoming by productStartDate. */
const resolveVisibleProductsForMember = products => {
    const valid = products.filter(p => !p.dateDeleted)
    if (valid.length === 0) return []

    const today = moment().startOf('day')

    const active = valid.filter(product => {
        const start = product.productStartDate ? moment(product.productStartDate).startOf('day') : null
        const end = product.productEndDate ? moment(product.productEndDate).startOf('day') : null

        if (start && end) {
            return start.isSameOrBefore(today) && end.isSameOrAfter(today)
        }
        if (start && !end) {
            return start.isSameOrBefore(today)
        }
        if (!start && end) {
            return end.isSameOrAfter(today)
        }
        return true
    })

    if (active.length > 0) return active

    const future = valid.filter(product => {
        if (!product.productStartDate) return false
        return moment(product.productStartDate).startOf('day').isAfter(today)
    })

    if (future.length > 0) {
        future.sort((a, b) => moment(a.productStartDate).valueOf() - moment(b.productStartDate).valueOf())
        return [future[0]]
    }

    return []
}

export const subscriptionSelector = id =>
    createSelector(subscriptionsHistorySelector, subscriptions => {
        const subscription = subscriptions.find(sub => id === sub.id)
        const { members } = subscription

        const updatedMembers = members.filter(member => {
            if (member.deletedDate || member.products.length < 1) {
                return false
            }

            member.products = resolveVisibleProductsForMember(member.products)

            return member.products.length > 0
        })

        return { ...subscription, members: updatedMembers }
    })

export const productsSelector = id =>
    createSelector(subscriptionSelector(id), subscription => subscription?.members || [])

export const paymentMethodSelector = id =>
    createSelector(subscriptionSelector(id), subscription =>
        subscription.paymentMethods ? subscription.paymentMethods[0] : {}
    )

export const invoicePaymentPendingSelector = createSelector(
    pendingSelector,
    pending => pending[INVOICE_PAYMENT_PROGRESS]
)

export const parkerDataForAssignSelector = data =>
    createSelector(subscriptionSelector(data.subscriptionId), subscription => {
        const { members } = subscription

        const member = members.findIndex(m => m.id === data.memberId)

        members[member].firstName = data.user?.firstName
        members[member].lastName = data.user?.lastName

        if (data.selfService) {
            members[member].emailAddress = data.user?.emailAddress
        }

        const fieldList = data?.selectedVehicles?.map(vehicle => {
            let fieldMap
            if (vehicle.isDeleted) {
                fieldMap = {
                    color: {
                        inactive: false,
                        value: vehicle.color,
                    },
                    model: {
                        inactive: false,
                        value: vehicle.model,
                    },

                    make: {
                        inactive: false,
                        value: vehicle.make,
                    },
                    country: {
                        code: 'country',
                        inactive: false,
                        value: vehicle.country,
                    },

                    state: {
                        value: vehicle.state,
                    },
                    plateNumber: {
                        inactive: false,
                        value: vehicle.tag,
                    },
                    vehicleId: {
                        inactive: false,
                        value: vehicle.id || v4(),
                        code: 'vehicleId',
                    },
                    plateNumberSearch: {
                        value: vehicle.tag,
                    },
                    isDeleted: {
                        value: true,
                    },
                }
            } else
                fieldMap = {
                    plateNumber: {
                        inactive: false,
                        value: vehicle.tag,
                    },
                    vehicleId: {
                        inactive: false,
                        value: vehicle.id || v4(),
                        code: 'vehicleId',
                    },
                    plateNumberSearch: {
                        value: vehicle.tag,
                    },
                }

            return { code: 'vehicle', inactive: false, fieldMap }
        })

        members[member].extended = {
            inactive: false,
            fieldMap: {
                vehicles: {
                    inactive: false,
                    fieldList,
                },
            },
        }

        return members
    })

export const assignParkerPendingSelector = createSelector(pendingSelector, pending => pending[ASSIGN_PARKER_PROGRESS])
export const removeParkerPendingSelector = createSelector(pendingSelector, pending => pending[REMOVE_PARKER_PROGRESS])
export const savePadPendingSelector = createSelector(pendingSelector, pending => pending[SAVE_PAD_DATA_PROGRESS])

export const assignedParkerSelector = (subscriptionId, memberId) =>
    createSelector(subscriptionSelector(subscriptionId), subscription => {
        const { members } = subscription

        if (members && members[0]) {
            const member = members.find(m => m.id === memberId)

            if (member) {
                const { firstName, lastName, emailAddress, extended } = member
                const vehicles = vehiclesEntityToArray(member)
                const selfServiceConfirmed = extended?.fieldMap?.selfService?.value || false

                const userData = {
                    firstName,
                    lastName,
                    emailAddress,
                    vehicles,
                    selfServiceConfirmed,
                    memberId,
                }

                return userData
            }
        }

        return {}
    })
